/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Static English translation resolver.
 *
 * Backs the English-only i18n shim used when the app is built with
 * `I18N_MODE=static`. It reads the single, statically-bundled `en-US` resource
 * (the same merged translations + extension translations the real i18next backend
 * would load) and resolves keys synchronously — no i18next core, no async backend,
 * no per-language chunking.
 *
 * Supported i18next semantics (the subset this template actually uses; audited
 * across ~300 call sites):
 * - Namespaced lookup: `useTranslation('cart').t('summary.title')` → `cart.summary.title`.
 * - Explicit namespace via colon: `t('cart:summary.title')`.
 * - English pluralization: `_one` when `count === 1`, else `_other`.
 * - Interpolation `{{var}}` and formatted `{{count, number}}` (→ `toLocaleString()`).
 * - `returnObjects: true` (returns the subtree instead of a string).
 * - i18next fallback: an unresolved key returns the key string itself.
 */
import enUS from '@/locales/en-US';

type Resource = Record<string, unknown>;

const resources = enUS as unknown as Resource;

/** English (US) is the only supported language in static mode. */
export const STATIC_LANGUAGE = 'en-US';

export interface TranslateOptions {
    count?: number;
    defaultValue?: string;
    returnObjects?: boolean;
    // Interpolation values plus i18next reserved keys.
    [key: string]: unknown;
}

/** Reserved i18next option keys that must not be treated as interpolation values. */
const RESERVED_OPTION_KEYS = new Set(['count', 'defaultValue', 'returnObjects', 'ns', 'context', 'lng', 'replace']);

function getByPath(obj: Resource, path: string): unknown {
    return path.split('.').reduce<unknown>((acc, segment) => {
        if (acc && typeof acc === 'object') return (acc as Resource)[segment];
        return undefined;
    }, obj);
}

/**
 * Interpolate `{{var}}` / `{{var, number}}` placeholders. Missing values render as
 * empty string (i18next default with `escapeValue:false`). React handles escaping.
 */
export function interpolate(template: string, options?: TranslateOptions): string {
    if (!options) return template;
    return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, expr: string) => {
        const [rawName, rawFormat] = expr.split(',').map((s) => s.trim());
        const value = options[rawName];
        if (value === undefined || value === null) return '';
        if (rawFormat === 'number' && typeof value === 'number') return value.toLocaleString();
        return String(value);
    });
}

/** Build the ordered list of candidate keys to try, applying English plural rules. */
function candidateKeys(resolvedKey: string, bareKey: string, options?: TranslateOptions): string[] {
    const bases = resolvedKey === bareKey ? [resolvedKey] : [resolvedKey, bareKey];
    if (typeof options?.count !== 'number') return bases;
    const suffix = options.count === 1 ? '_one' : '_other';
    // Prefer the pluralized variant, then the base (some keys have no plural forms).
    return bases.flatMap((base) => [`${base}${suffix}`, base]);
}

/**
 * Resolve a translation key to its English value. Mirrors i18next's default
 * resolution closely enough for a single static language.
 *
 * @param namespace hook namespace (from `useTranslation(ns)`), if any
 * @param key translation key, optionally `ns:key`
 */
export function translate(namespace: string | undefined, key: string, options?: TranslateOptions): unknown {
    // Namespace precedence mirrors i18next: an explicit `ns:` in the key wins, then
    // the hook namespace, then `options.ns` (e.g. an unbound `t(key, { ns })` call).
    let ns = namespace ?? (typeof options?.ns === 'string' ? options.ns : undefined);
    let bareKey = key;
    const colon = key.indexOf(':');
    if (colon !== -1) {
        ns = key.slice(0, colon);
        bareKey = key.slice(colon + 1);
    }

    const resolvedKey = ns ? `${ns}.${bareKey}` : bareKey;

    let value: unknown;
    for (const candidate of candidateKeys(resolvedKey, bareKey, options)) {
        value = getByPath(resources, candidate);
        if (value !== undefined) break;
    }

    if (value === undefined) value = options?.defaultValue;
    if (value === undefined) return key; // i18next returns the key when unresolved

    if (typeof value === 'object' && value !== null) {
        return options?.returnObjects ? value : key;
    }

    return interpolate(String(value), options);
}

/** Whether a key resolves to a real translation (used by `i18n.exists`). */
export function exists(namespace: string | undefined, key: string): boolean {
    return translate(namespace, key) !== key;
}

/**
 * Resource bundle for a namespace (or the whole dictionary when omitted) — backs
 * `i18n.getResourceBundle`. `root.tsx` reads the `routeError` subtree to seed the
 * error page's translations, so returning `{}` would silently drop any customized
 * `routeError.*` strings.
 */
export function getBundle(namespace?: string): Record<string, unknown> {
    if (!namespace) return resources;
    const bundle = resources[namespace];
    return bundle && typeof bundle === 'object' ? (bundle as Record<string, unknown>) : {};
}

/** Raw (uninterpolated) string for a key — used by `<Trans>` before tag substitution. */
export function rawString(namespace: string | undefined, key: string, options?: TranslateOptions): string {
    let ns = namespace ?? (typeof options?.ns === 'string' ? options.ns : undefined);
    let bareKey = key;
    const colon = key.indexOf(':');
    if (colon !== -1) {
        ns = key.slice(0, colon);
        bareKey = key.slice(colon + 1);
    }
    const resolvedKey = ns ? `${ns}.${bareKey}` : bareKey;
    for (const candidate of candidateKeys(resolvedKey, bareKey, options)) {
        const value = getByPath(resources, candidate);
        if (typeof value === 'string') return value;
    }
    return key;
}

/** Whether the reserved-key filter should treat `k` as an interpolation value. */
export function isInterpolationKey(k: string): boolean {
    return !RESERVED_OPTION_KEYS.has(k);
}
